/**
 * Validation utility functions for form inputs
 * Provides consistent validation across the application
 */

/**
 * Validates an email address
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether the email is valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates a password meets minimum requirements
 * @param {string} password - Password to validate
 * @returns {object} - Validation result with isValid flag and message
 */
export const validatePassword = (password) => {
  if (!password) {
    return { isValid: false, message: 'Password is required' };
  }
  
  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters long' };
  }
  
  // Check for at least one number
  if (!/\d/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one number' };
  }
  
  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  }
  
  // Check for at least one special character
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one special character' };
  }
  
  return { isValid: true, message: 'Password is valid' };
};

/**
 * Validates a URL
 * @param {string} url - URL to validate
 * @returns {boolean} - Whether the URL is valid
 */
export const isValidUrl = (url) => {
  if (!url) return true; // Empty URLs are considered valid (optional fields)
  
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Validates a phone number (basic format check)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - Whether the phone number is valid
 */
export const isValidPhone = (phone) => {
  if (!phone) return true; // Empty phone numbers are considered valid (optional fields)
  
  // Allow digits, spaces, dashes, parentheses, and plus sign
  const phoneRegex = /^[0-9\s\-+()]{10,15}$/;
  return phoneRegex.test(phone);
};

/**
 * Validates a date is not in the past
 * @param {string} dateStr - Date string to validate
 * @returns {boolean} - Whether the date is valid and not in the past
 */
export const isValidFutureDate = (dateStr) => {
  if (!dateStr) return false;
  
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  
  return !isNaN(date) && date >= today;
};

/**
 * Validates a numeric value
 * @param {string|number} value - Value to validate
 * @param {number} min - Minimum allowed value (optional)
 * @param {number} max - Maximum allowed value (optional)
 * @returns {boolean} - Whether the value is a valid number within range
 */
export const isValidNumber = (value, min = null, max = null) => {
  if (value === null || value === undefined || value === '') return false;
  
  const num = Number(value);
  if (isNaN(num)) return false;
  
  if (min !== null && num < min) return false;
  if (max !== null && num > max) return false;
  
  return true;
};

/**
 * Validates required fields in a form object
 * @param {object} formData - Form data object
 * @param {array} requiredFields - Array of required field names
 * @returns {object} - Object with isValid flag and errors object
 */
export const validateRequiredFields = (formData, requiredFields) => {
  const errors = {};
  let isValid = true;
  
  requiredFields.forEach(field => {
    if (!formData[field] || 
        (typeof formData[field] === 'string' && formData[field].trim() === '') ||
        (Array.isArray(formData[field]) && formData[field].length === 0)) {
      errors[field] = 'This field is required';
      isValid = false;
    }
  });
  
  return { isValid, errors };
};

/**
 * Validates social media URLs
 * @param {string} url - URL to validate
 * @param {string} platform - Social media platform
 * @returns {boolean} - Whether the URL is valid for the platform
 */
export const isValidSocialMediaUrl = (url, platform) => {
  if (!url) return true; // Empty URLs are considered valid (optional fields)
  
  try {
    const parsedUrl = new URL(url);
    
    switch (platform) {
      case 'instagram':
        return parsedUrl.hostname.includes('instagram.com');
      case 'facebook':
        return parsedUrl.hostname.includes('facebook.com') || parsedUrl.hostname.includes('fb.com');
      case 'tiktok':
        return parsedUrl.hostname.includes('tiktok.com');
      case 'youtube':
        return parsedUrl.hostname.includes('youtube.com') || parsedUrl.hostname.includes('youtu.be');
      case 'soundcloud':
        return parsedUrl.hostname.includes('soundcloud.com');
      case 'bandcamp':
        return parsedUrl.hostname.includes('bandcamp.com');
      case 'spotify':
        return parsedUrl.hostname.includes('spotify.com') || parsedUrl.hostname.includes('open.spotify.com');
      default:
        return true; // For other platforms, just check if it's a valid URL
    }
  } catch (e) {
    return false;
  }
};
